import { useCallback, useMemo } from "react";
import { useGetCompanySpecificIndustryList } from "@/api/services/global/master";
import { useEnum } from "@/api/services/global/enum";
import { useCaseFilterOptions } from "@/api/services/main/case";
import { useGetUserListPersonInCharge } from "@/api/services/main/user";
import { useGetProvinceAndMunicipalityList } from "@/hooks/useGetProvinceAndMunicipalityList";
import { truncateWithEllipsis } from "@/utils/string";
import { TOption } from "@/types/common";
import { FILTER_LABEL_MAX_LENGTH, FILTER_LABEL_FULL_WIDTH_MAX_LENGTH } from "@/features/case/components/CaseList/case-list-filter-shared";
import { CASE_STATUS } from "@/features/case/constants/caseStatus";

export const useCaseListFilterMasterData = () => {
  const { companySpecificIndustryOptions } = useGetCompanySpecificIndustryList();
  const { data: caseStatusOptions } = useEnum("ECaseStatus");
  const { managementNumberOptions, caseNameOptions, jobNameOptions, companyNameOptions, recruitmentNumberOptions } = useCaseFilterOptions();
  const { userOptions: personInChargeOptions } = useGetUserListPersonInCharge();
  const { provinceOptions, getMunicipalityOptionsByProvince } = useGetProvinceAndMunicipalityList();

  const truncateOpts = useCallback(
    (options: TOption[], maxLength = FILTER_LABEL_MAX_LENGTH) =>
      options.map((opt) => ({ ...opt, label: truncateWithEllipsis(opt.label, maxLength) })),
    [],
  );

  // Range options for 提案人数 and 募集人数
  const countRangeOptions: TOption[] = useMemo(
    () => [
      { label: "0人", value: "0" },
      { label: "1-3人", value: "1-3" },
      { label: "4-9人", value: "4-9" },
      { label: "10人以上", value: "10+" },
    ],
    [],
  );

  const truncatedOptions = useMemo(
    () => ({
      caseStatus: truncateOpts((caseStatusOptions ?? []).filter((item) => Number(item.value) !== CASE_STATUS.JOB_POSTED)),
      managementNumber: truncateOpts(managementNumberOptions, FILTER_LABEL_FULL_WIDTH_MAX_LENGTH),
      caseName: truncateOpts(caseNameOptions, FILTER_LABEL_FULL_WIDTH_MAX_LENGTH),
      jobName: truncateOpts(jobNameOptions, FILTER_LABEL_FULL_WIDTH_MAX_LENGTH),
      companyName: truncateOpts(companyNameOptions),
      industry: truncateOpts(companySpecificIndustryOptions),
      recruitmentNumber: countRangeOptions,
      caseMatchCount: countRangeOptions,
      personInCharge: truncateOpts(personInChargeOptions),
    }),
    [
      truncateOpts,
      caseStatusOptions,
      managementNumberOptions,
      caseNameOptions,
      jobNameOptions,
      companyNameOptions,
      companySpecificIndustryOptions,
      countRangeOptions,
      personInChargeOptions,
    ],
  );

  const rawOptions = useMemo(
    () => ({
      caseStatus: (caseStatusOptions ?? []).filter((item) => Number(item.value) !== CASE_STATUS.JOB_POSTED),
      companyName: companyNameOptions ?? [],
      industry: companySpecificIndustryOptions,
      personInCharge: personInChargeOptions,
      workplaceProvince: provinceOptions,
      countRange: countRangeOptions,
      recruitmentNumber: countRangeOptions,
      caseMatchCount: countRangeOptions,
    }),
    [caseStatusOptions, companyNameOptions, companySpecificIndustryOptions, personInChargeOptions, provinceOptions, countRangeOptions],
  );

  return { truncatedOptions, rawOptions, getMunicipalityOptionsByProvince };
};
